<template>
  <div class="sonar-title-bar">
    <img
      src="../../assets/images/ine-logo.png"
      class="sonar-title-bar__ine-logo"
    />
    <div v-if="showAssessmentInfo" class="sonar-title-bar__info-wrapper">
      <span class="sonar-title-bar__question-position">{{
        questionLabel
      }}</span>
      <ClockIcon class="sonar-title-bar__timer-icon" />

      <div
        v-if="showTimer"
        :class="[
          'sonar-title-bar__timer',
          (isAssessmentOutOfTime || isAssessmentRunningOutOfTime) &&
            'sonar-title-bar__timer--warning',
        ]"
      >
        {{ assessmentTimerLabel }}
      </div>
      <SwitchCheckbox
        v-model="showTimer"
        v-tooltip="{
          content: `${timerTooltipLabel}`,
          placement: 'bottom-center',
        }"
        class="sonar-title-bar__time-switch"
      />
    </div>
    <div
      v-if="showCorrectAnswersToggle"
      class="sonar-title-bar__answers-wrapper"
      @click="toggleCorrectAnswers()"
    >
      <div class="ine-checkbox__input sonar-title-bar__checkbox-input">
        <input
          type="checkbox"
          name="correct-answers-checkbox"
          :value="displayCorrectAnswers"
          :checked="displayCorrectAnswers"
        />
        <span class="ine-checkbox__control sonar-title-bar__checkbox-control">
          <CheckboxTick />
        </span>
      </div>
      <div class="sonar-title-bar__answers-label">Reveal correct answers</div>
    </div>
    <div class="sonar-title-bar__btns">
      <button
        class="sonar-title-bar__close-modal-btn"
        @click="$emit('close-modal')"
      >
        <XIcon class="sonar-title-bar__close-icon" />
      </button>
    </div>
    <div
      :class="[
        'sonar-title-bar__progress-bar',
        progressCompleted && 'sonar-title-bar__progress-bar--completed',
      ]"
      :style="`width: ${progressBarWidth}%`"
    ></div>
  </div>
</template>

<script>
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import { XIcon, ClockIcon } from 'vue-feather-icons'
import { mapGetters } from 'vuex'
import { duration } from '@/helpers'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'

export default {
  name: 'SonarTitleBar',
  components: {
    XIcon,
    ClockIcon,
    SwitchCheckbox,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
  },
  props: {
    isAssessmentRunning: {
      type: Boolean,
      required: true,
    },
    timer: {
      type: Number,
      required: false,
    },
    hasLab: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      showTimer: true,
    }
  },
  methods: {
    toggleCorrectAnswers() {
      this.$store.dispatch(
        'sonar/setDisplayCorrectAnswers',
        !this.displayCorrectAnswers
      )
    },
  },
  computed: {
    ...mapGetters({
      isAssessmentOutOfTime: 'sonar/isAssessmentOutOfTime',
      isLastQuestionAnswered: 'sonar/isLastQuestionAnswered',
      canAnswerQuestions: 'sonar/canAnswerQuestions',
      currentQuestion: 'sonar/currentQuestion',
      assessmentAttempt: 'sonar/assessmentAttempt',
      isAssessmentStarted: 'sonar/isAssessmentStarted',
      displayCorrectAnswers: 'sonar/displayCorrectAnswers',
      isAdminOrContentCreator: 'auth/isAdminOrContentCreator',
    }),
    currentPosition() {
      return this.currentQuestion.position
    },
    totalQuestions() {
      return this.assessmentAttempt?.questions?.length
    },
    questionLabel() {
      return `Q ${this.currentPosition}/${this.totalQuestions}`
    },
    progressBarWidth() {
      const ratio = 100 / this.totalQuestions
      return this.isLastQuestionAnswered
        ? 100
        : ratio * (this.currentPosition - 1)
    },
    assessmentTimerLabel() {
      if (this.isAssessmentOutOfTime) return 'Time’s up!'
      return this.assessmentTimer
    },
    assessmentTimer() {
      const formattedTime = duration.onlymmss(this.timer)
      return `${formattedTime}${this.isAssessmentRunningOutOfTime ? 's' : ''}`
    },
    isAssessmentRunningOutOfTime() {
      return this.timer < 6
    },
    progressCompleted() {
      return this.totalQuestions === this.currentPosition
    },
    showAssessmentInfo() {
      return this.isAssessmentRunning
    },
    showCorrectAnswersToggle() {
      return this.isAdminOrContentCreator && this.showAssessmentInfo
    },
    timerTooltipLabel() {
      const action = this.showTimer ? 'Hide' : 'Show'
      return `${action} timer`
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-title-bar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  padding: 18px 24px;
  z-index: 204;
  background: var(--menu-gradient);
  border-bottom: none;
  height: var(--sonar-modal-navbar-height);
  border-bottom: 10px solid var(--tundora-gray);
  &__progress-bar {
    position: absolute;
    height: 10px;
    bottom: -10px;
    left: 0;
    width: 0;
    background: var(--positive-light);
    border-radius: 0px 10px 10px 0px;
    transition: width 0.4s ease-in-out;
    &--completed {
      border-radius: 0;
    }
  }
  &__ine-logo {
    width: 92px;
    margin-right: 38px;
  }
  &__content {
    display: flex;
    align-items: center;
    flex-grow: 1;
    :global(.action-loader__loader) {
      margin-top: 14px;
    }
    &-loaded {
      display: flex;
      align-items: center;
      flex-grow: 1;
    }
  }
  &__icon {
    width: 28px;
    margin-top: 5px;
    &--els,
    &--pta {
      width: 18px;
    }
    .svg {
      fill: white;
    }
  }
  &__title-wrapper {
    flex-grow: 1;
  }
  &__title {
    margin: 0;
    padding: 0;
    font-size: 1rem;
    line-height: 1rem;
    display: none;
    color: white;
    margin-left: 15px;
    @include mobile {
      display: block;
    }
    @include desktop {
      font-size: 18px;
      line-height: 2.5rem;
      margin-top: 3px;
    }
  }
  &__info-wrapper {
    position: absolute;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 10px;
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
    font-weight: 500;
  }
  &__question-position {
    display: flex;
    justify-content: flex-end;
    border-right: 2px solid var(--perfect-gray);
    padding-right: 10px;
  }
  &__timer {
    display: flex;
    align-items: center;
    min-width: 48px;
    gap: 6.75px;
    color: var(--white);
    font-weight: 500;
    &--warning {
      color: var(--carnation-sonar);
    }
  }
  &__timer-icon {
    width: 16.5px;
    height: 16.5px;
  }
  &__time-switch {
    display: flex;
  }
  &__btns {
    display: flex;
    align-items: center;
    margin-left: 20px;
    gap: 16px;
    z-index: 10;
  }
  &__submit {
    height: 36px;
    padding: 8px 16px;
  }
  &__answers-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-left: auto;
    color: var(--white);
    font-size: rem(14);
    z-index: 50;
    cursor: pointer;
  }
  &__answer-checkbox {
    width: 24px;
    height: 24px;
  }
  &__checkbox {
    &-input {
      cursor: pointer;
    }
    &-control {
      width: 24px;
      height: 24px;
      border-radius: 8px;
      pointer-events: none;
      border: 1px solid var(--white);
      svg {
        width: 8px;
      }
    }
  }
  &__close-text {
    display: none;
    color: var(--lighter-gray);
    font-size: 16px;
    margin: 0;
    @include desktop {
      display: block;
    }
  }
  &__close-modal-btn {
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      opacity: 0.5;
    }
  }
  &__close-icon {
    margin: 8px 0 0 0;
    color: var(--white);
  }
}
</style>
